import React, { useCallback, useMemo } from 'react';

import { useSnackbar } from 'notistack';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import useAccommodationPropertyDetailsContext from '~/components/Accommodation/Pages/PropertyDetails/context/useAccommodationPropertyDetailsContext';
import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_HOTEL_COORDINATOR } from '~/consts/roles';

import {
  useGetPropertyContentSources,
  useUpdatePropertyContentSources,
} from '~/hooks/apis/accommodation/usePropertyContentSources';

import { UpdatePropertyContentSourcesPayload } from '~/services/AccommodationService';

import ContentSourceFieldRow, {
  getActiveSource,
  getSelectableSources,
} from '../AccommodationPropertyContentSources/ContentSourceFieldRow';
import useContentSourceFieldStates from '../AccommodationPropertyContentSources/useContentSourceFieldStates';

const EDIT_ROLES = [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_HOTEL_COORDINATOR];
const EXCLUDE_CUSTOM_FIELDS = ['address'];
const CUSTOM_CONTENT_FIELD_ORDER = [
  'tagline',
  'description',
  'additionalDescription',
  'locationDescription',
  'facilities',
  'highlights',
  'finePrint',
  'finePrintCheckIn',
  'finePrintCheckOut',
  'finePrintCheckInInstructions',
  'finePrintFees',
  'finePrintPets',
  'finePrintGeneral',
  'finePrintSafety',
  'finePrintPolicies',
  'finePrintInclusions',
  'finePrintCancellation',
  'finePrintDateChanges',
];
const CUSTOM_CONTENT_FIELD_ORDER_BY_FIELD = new Map(CUSTOM_CONTENT_FIELD_ORDER.map((field, index) => [field, index]));
const isFinePrintSubField = (field: string) => field.startsWith('finePrint') && field !== 'finePrint';
const FINE_PRINT_SUB_FIELD_LABELS: Record<string, string> = {
  finePrintCheckIn: 'Check-in',
  finePrintCheckOut: 'Check-out',
  finePrintCheckInInstructions: 'Check-in Instructions',
  finePrintFees: 'Fees',
  finePrintPets: 'Pets',
  finePrintGeneral: 'General',
  finePrintSafety: 'Cleaning and Safety',
  finePrintPolicies: 'Policies',
  finePrintInclusions: 'Inclusions',
  finePrintCancellation: 'Cancellation Policy',
  finePrintDateChanges: 'Date Changes',
};

export default function AccommodationPropertyContentSelection() {
  const { enqueueSnackbar } = useSnackbar();
  const { propertyDetails } = useAccommodationPropertyDetailsContext();
  const {
    data: contentSourcesData,
    isLoading: isContentSourcesLoading,
    error: contentSourcesError,
  } = useGetPropertyContentSources({ propertyId: propertyDetails.id });
  const updateMutation = useUpdatePropertyContentSources();
  const {
    getChangedFields,
    getFieldState,
    handleFieldChange,
    hasChanges: hasContentSourceChanges,
  } = useContentSourceFieldStates(contentSourcesData?.propertyFields, contentSourcesData?.availableSources);
  const structuredFields = useMemo(
    () =>
      contentSourcesData?.propertyFields
        .filter((fieldSelection) => !EXCLUDE_CUSTOM_FIELDS.includes(fieldSelection.field))
        .slice()
        .sort((a, b) => {
          const aOrder = CUSTOM_CONTENT_FIELD_ORDER_BY_FIELD.get(a.field) ?? Number.MAX_SAFE_INTEGER;
          const bOrder = CUSTOM_CONTENT_FIELD_ORDER_BY_FIELD.get(b.field) ?? Number.MAX_SAFE_INTEGER;

          return aOrder - bOrder;
        }) ?? [],
    [contentSourcesData?.propertyFields],
  );
  const finePrintSubFields = useMemo(
    () => structuredFields.filter((fieldSelection) => isFinePrintSubField(fieldSelection.field)),
    [structuredFields],
  );
  const topLevelStructuredFields = useMemo(
    () => structuredFields.filter((fieldSelection) => !isFinePrintSubField(fieldSelection.field)),
    [structuredFields],
  );

  const handleSaveContentSources = useCallback(() => {
    if (!contentSourcesData) {
      return;
    }

    const changedFields = getChangedFields() as UpdatePropertyContentSourcesPayload['fields'];
    if (changedFields.length === 0) {
      return;
    }

    updateMutation.mutate(
      { propertyId: propertyDetails.id, data: { fields: changedFields } },
      {
        onSuccess: () => {
          enqueueSnackbar('Property content sources updated', { variant: 'success' });
        },
        onError: (err: Error) => {
          enqueueSnackbar(`Failed to update: ${err.message}`, { variant: 'error' });
        },
      },
    );
  }, [contentSourcesData, getChangedFields, propertyDetails.id, updateMutation, enqueueSnackbar]);

  return (
    <Stack direction="column" spacing={4}>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Select the source override.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fields left empty have the best source selected when serving the content.
            </Typography>
          </Box>
          <PermissionedComponent requiredRoles={EDIT_ROLES}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveContentSources}
              disabled={updateMutation.isPending || !hasContentSourceChanges}
            >
              Save All
            </Button>
          </PermissionedComponent>
        </Stack>

        {isContentSourcesLoading && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {contentSourcesError && (
          <Alert severity="error">
            Error loading content sources:{' '}
            {contentSourcesError instanceof Error ? contentSourcesError.message : 'Unknown error'}
          </Alert>
        )}

        {contentSourcesData && (
          <PermissionedComponent requiredRoles={EDIT_ROLES}>
            <Stack spacing={1}>
              {topLevelStructuredFields.map((fieldSelection) => {
                const activeSource = getActiveSource(fieldSelection, contentSourcesData.availableSources);

                return (
                  <React.Fragment key={fieldSelection.field}>
                    <ContentSourceFieldRow
                      fieldSelection={fieldSelection}
                      activeSource={activeSource}
                      availableSources={getSelectableSources(fieldSelection, contentSourcesData.availableSources)}
                      fieldState={getFieldState(fieldSelection)}
                      onChange={handleFieldChange}
                      isPending={updateMutation.isPending}
                      allowEditSourceAsCustom
                    />
                    {fieldSelection.field === 'finePrint' && finePrintSubFields.length > 0 && (
                      <Accordion variant="outlined">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1">Fine Print Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={1}>
                            {finePrintSubFields.map((finePrintSubField) => {
                              const finePrintSubFieldActiveSource = getActiveSource(
                                finePrintSubField,
                                contentSourcesData.availableSources,
                              );

                              return (
                                <ContentSourceFieldRow
                                  key={finePrintSubField.field}
                                  fieldSelection={finePrintSubField}
                                  activeSource={finePrintSubFieldActiveSource}
                                  availableSources={getSelectableSources(
                                    finePrintSubField,
                                    contentSourcesData.availableSources,
                                  )}
                                  fieldState={getFieldState(finePrintSubField)}
                                  labelOverride={FINE_PRINT_SUB_FIELD_LABELS[finePrintSubField.field]}
                                  onChange={handleFieldChange}
                                  isPending={updateMutation.isPending}
                                  allowEditSourceAsCustom
                                />
                              );
                            })}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </React.Fragment>
                );
              })}
              {!topLevelStructuredFields.some((fieldSelection) => fieldSelection.field === 'finePrint') &&
                finePrintSubFields.length > 0 && (
                  <Accordion variant="outlined">
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1">Fine Print Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1}>
                        {finePrintSubFields.map((fieldSelection) => {
                          const activeSource = getActiveSource(fieldSelection, contentSourcesData.availableSources);

                          return (
                            <ContentSourceFieldRow
                              key={fieldSelection.field}
                              fieldSelection={fieldSelection}
                              activeSource={activeSource}
                              availableSources={getSelectableSources(
                                fieldSelection,
                                contentSourcesData.availableSources,
                              )}
                              fieldState={getFieldState(fieldSelection)}
                              labelOverride={FINE_PRINT_SUB_FIELD_LABELS[fieldSelection.field]}
                              onChange={handleFieldChange}
                              isPending={updateMutation.isPending}
                              allowEditSourceAsCustom
                            />
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}
            </Stack>
          </PermissionedComponent>
        )}
      </Box>
    </Stack>
  );
}
